/**
 * newVersion — does the server run a newer build than this tab? PURE helpers.
 *
 * Every build gives the entry script a new hashed name (`/assets/index-XXXX.js`).
 * `index.html` is served `no-store`, so reading it shows what the server runs
 * NOW; the entry `<script>` in this document shows what the tab runs.
 *
 * Why (Manak 2026-10-23): the Allocation Board fix went live at 10:56 IST, and
 * a tab opened at 10:30 kept running the old code for an hour — two "reloads"
 * never reached the server. `chunkReload.ts` only fires when an old chunk is
 * MISSING, and deploys keep old assets, so it never fired.
 */

const ENTRY = /\/assets\/(index-[A-Za-z0-9_-]+\.js)/;

/** The entry script a page of HTML loads, or null (e.g. the dev page). */
export function entryFromHtml(html: string): string | null {
  const tag = html.match(/<script\b[^>]*\btype=["']module["'][^>]*>/gi) ?? [];
  for (const t of tag) {
    const m = t.match(ENTRY);
    if (m) return m[1]!;
  }
  return null;
}

/** The entry script this tab is running, or null when not a built page. */
export function runningEntry(doc: Document = document): string | null {
  for (const s of Array.from(doc.querySelectorAll('script[type="module"][src]'))) {
    const m = (s.getAttribute('src') ?? '').match(ENTRY);
    if (m) return m[1]!;
  }
  return null;
}

/** New only when BOTH are known and differ. Unknown never counts as new. */
export function isNewVersion(running: string | null, served: string | null): boolean {
  return !!running && !!served && running !== served;
}

type FetchLike = (url: string, init?: RequestInit) => Promise<{ ok: boolean; text: () => Promise<string> }>;

/** The entry the server serves right now; null on any failure. */
export async function fetchServedEntry(fetchImpl: FetchLike = fetch as unknown as FetchLike): Promise<string | null> {
  try {
    const r = await fetchImpl('/index.html', { cache: 'no-store', credentials: 'same-origin' });
    if (!r.ok) return null;
    return entryFromHtml(await r.text());
  } catch {
    return null;
  }
}
